import { Phone, Mail, MapPin, ArrowUp } from "lucide-react";
import { Crescent, scrollToId } from "./Reveal";
import { useLang } from "@/i18n";

const SERVICES = ["Dermatology & Cosmetology", "ENT", "Pathology", "ECG", "In-House Pharmacy", "General OPD"];

export default function Footer() {
    const { t } = useLang();

    const LINKS = [
        { id: "services", label: t.nav.services },
        { id: "doctors", label: t.nav.doctors },
        { id: "gallery", label: t.nav.gallery },
        { id: "appointment", label: t.nav.book },
        { id: "location", label: t.nav.location },
    ];

    return (
        <footer data-testid="site-footer" className="relative overflow-hidden bg-brand-navy pt-20 text-white">
            <Crescent className="pointer-events-none absolute -right-24 -top-24 h-[420px] w-[420px] text-white/[0.03]" />

            <div className="relative mx-auto max-w-7xl px-5 md:px-8">
                <div className="grid grid-cols-1 gap-12 pb-16 md:grid-cols-12">
                    <div className="md:col-span-5">
                        <img
                            src="/signita-logo-white.png"
                            alt="Signita Wellness logo"
                            data-testid="footer-logo"
                            className="h-16 w-auto object-contain md:h-20"
                        />
                        <p className="mt-6 max-w-sm text-sm leading-relaxed text-slate-400">{t.footer.desc}</p>
                    </div>

                    <div className="md:col-span-2">
                        <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-brand-lime">{t.footer.explore}</p>
                        <ul className="mt-5 space-y-3">
                            {LINKS.map((l) => (
                                <li key={l.id}>
                                    <button
                                        data-testid={`footer-link-${l.id}`}
                                        onClick={() => scrollToId(l.id)}
                                        className="text-sm text-slate-300 transition-colors duration-200 hover:text-brand-lime"
                                    >
                                        {l.label}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="md:col-span-2">
                        <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-brand-lime">{t.footer.services}</p>
                        <ul className="mt-5 space-y-3">
                            {SERVICES.map((s) => (
                                <li key={s} className="text-sm text-slate-300">
                                    {s}
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="md:col-span-3">
                        <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-brand-lime">{t.footer.contact}</p>
                        <ul className="mt-5 space-y-4 text-sm text-slate-300">
                            <li className="flex items-start gap-3">
                                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand-lime" />
                                Near Oscar Hospital, Kasarvadavali, Ghodbunder Road, Thane West 400615
                            </li>
                            <li className="flex items-center gap-3 font-mono">
                                <Phone className="h-4 w-4 shrink-0 text-brand-lime" />
                                <span>
                                    <a data-testid="footer-phone-1" href="tel:+917208991091" className="transition-colors hover:text-brand-lime">
                                        7208991091
                                    </a>
                                    {" · "}
                                    <a data-testid="footer-phone-2" href="tel:+917208991092" className="transition-colors hover:text-brand-lime">
                                        7208991092
                                    </a>
                                </span>
                            </li>
                            <li className="flex items-center gap-3">
                                <Mail className="h-4 w-4 shrink-0 text-brand-lime" />
                                <a
                                    data-testid="footer-email"
                                    href="mailto:hello@signitawellness.in"
                                    className="transition-colors hover:text-brand-lime"
                                >
                                    hello@signitawellness.in
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="flex flex-col items-center justify-between gap-4 border-t border-white/10 py-8 md:flex-row">
                    <p className="text-xs text-slate-500">© {new Date().getFullYear()} Signita Wellness · www.signitawellness.in</p>
                    <button
                        data-testid="footer-back-to-top"
                        onClick={() => (window.__lenis ? window.__lenis.scrollTo(0, { duration: 1.4 }) : window.scrollTo({ top: 0, behavior: "smooth" }))}
                        className="group flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-xs font-semibold text-slate-300 transition-all duration-300 hover:border-brand-lime hover:text-brand-lime"
                    >
                        {t.footer.backTop}
                        <ArrowUp className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-y-0.5" />
                    </button>
                </div>
            </div>
        </footer>
    );
}
